/**
 * Timesheets — PM weekly approval + Miter push staging.
 *
 * Layout:
 *  ┌── Mode banner (DRY-RUN / SANDBOX / LIVE)
 *  ├── Week picker + summary chips (techs, hours, ready, blocked)
 *  ├── One row per tech for the selected week:
 *  │     - 🟢 / 🔴 indicator  (green = auto-review passed, red = needs PM eyes)
 *  │     - Mon–Sun cell strip with daily hours
 *  │     - Auto-review reasons (mismatched lunch, missing manual, etc.)
 *  │     - "Approve all green" + per-tech "Approve" / "Review" buttons
 *  └── Push to Miter button — disabled in DRY-RUN to make it crystal-clear
 *      that previews land in the Mock Inbox, not real payroll.
 *
 * Auto-review v1 is intentionally conservative — every check ships with
 * an obvious explanation so the PM knows exactly why a row is flagged.
 * Tunable thresholds will land in Settings → Timekeep later.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { format, addDays, startOfWeek, parseISO } from "date-fns";
import { ChevronLeft, ChevronRight, CheckCircle2, Send, Loader2, RefreshCw, Inbox, ShieldCheck, ShieldAlert, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import Segmented from "@/components/common/Segmented";
import SearchBar from "@/components/common/SearchBar";
import { deepMatch } from "@/components/common/Highlight";
import { DateJump } from "@/components/common/DateJump";
import { btn } from "@/components/common/btn";
import { WeekDayHeader, WeekCornerHeader, TechCell, weekGridCols } from "@/components/common/WeekGrid";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthContext";
import {
  listTimesheets,
  approveTimesheet,
  getTechnicians,
  getMiterConfigStatus,
  getMiterBatchPreview,
  pushMiterBatch,
  getMiterMockInbox,
  clearMiterMockInbox,
} from "@/lib/api";

const STATUS_BADGE = {
  open: "bg-slate-100 text-slate-500",
  submitted: "bg-blue-100 text-blue-700",
  flagged: "bg-rose-100 text-rose-700",
  approved: "bg-emerald-100 text-emerald-700",
  ready_to_push: "bg-emerald-100 text-emerald-700",
  pushed: "bg-violet-100 text-violet-700",
  rejected: "bg-slate-200 text-slate-500",
};

// ── Auto-review engine v1 ────────────────────────────────────────────────
// Each rule returns a short tag (no prose). Keep tags ≤14 chars so the
// flagged-reason line stays readable on small screens.
function autoReviewDay(ts) {
  const reasons = [];
  const tech = parseFloat(ts.tech_entered_hours || 0);
  const gps = parseFloat(ts.gps_hours || 0);
  const status = ts.status;
  const lunch = parseInt(ts.lunch_minutes || 0, 10);

  if (tech > 12) reasons.push(`Day >12h`);
  if (tech > 0 && gps === 0) reasons.push(`No GPS`);
  if (tech > 0 && gps > 0 && (tech - gps) > 0.5) reasons.push(`GPS Δ ${(tech - gps).toFixed(1)}h`);
  if (status === "flagged" && ts.flagged_reason) reasons.push(ts.flagged_reason);
  if (tech >= 6 && lunch === 0 && ts.took_lunch === false) reasons.push(`No lunch ≥6h`);
  if (tech > 0 && tech < 4) reasons.push(`Short ${tech}h`);

  return { ok: reasons.length === 0, reasons };
}

// Mode chip — sits in the toolbar beside Push (was a full-width banner).
const MODE_CHIP = {
  live: ["LIVE", "bg-rose-100 text-rose-800 border-rose-300", ShieldAlert, "Pushes go to real Miter production. Confirmation required before each push."],
  sandbox: ["SANDBOX", "bg-amber-100 text-amber-800 border-amber-300", ShieldCheck, "HTTP calls go out to a non-production host. Real payroll is safe."],
  dry_run: ["DRY RUN", "bg-emerald-100 text-emerald-800 border-emerald-300", ShieldCheck, "Payloads are simulated only — no HTTP call leaves the app."],
};
function ModeChip({ mode }) {
  const [label, cls, Icon, title] = MODE_CHIP[mode] || MODE_CHIP.dry_run;
  return (
    <span className={`inline-flex h-7 items-center gap-1 rounded-md border px-2 text-[10px] font-bold tracking-wide ${cls}`} title={title} data-testid={`mode-chip-${mode === "dry_run" ? "dry-run" : mode}`}>
      <Icon className="w-3 h-3" />{label}
    </span>
  );
}

export default function TimesheetsPage() {
  const [weekStart, setWeekStart] = useState(() => format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd"));
  const [technicians, setTechnicians] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const { can } = useAuth();
  const payrollAllowed = can("integrations.miter", "read");
  const [config, setConfig] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showInbox, setShowInbox] = useState(false);
  const [inbox, setInbox] = useState([]);

  const weekEnd = useMemo(() => format(addDays(parseISO(weekStart), 6), "yyyy-MM-dd"), [weekStart]);
  const weekDays = useMemo(() => {
    const all = Array.from({ length: 7 }, (_, i) => addDays(parseISO(weekStart), i));
    const used = new Set(timesheets.map(t => t.date));
    return all.filter(d => { const dow = d.getDay(); return (dow !== 0 && dow !== 6) || used.has(format(d, "yyyy-MM-dd")); });
  }, [weekStart, timesheets]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Lead techs approve time but never touch payroll — skip the Miter
      // calls entirely for them (the backend blocks them anyway).
      const [techRes, tsRes, cfgRes, prevRes] = await Promise.all([
        getTechnicians(),
        listTimesheets({ start_date: weekStart, end_date: weekEnd }),
        payrollAllowed ? getMiterConfigStatus() : Promise.resolve({ data: null }),
        payrollAllowed ? getMiterBatchPreview(weekStart) : Promise.resolve({ data: null }),
      ]);
      setTechnicians(techRes.data.filter(t => !t.is_archived));
      setTimesheets(tsRes.data || []);
      setConfig(cfgRes.data);
      setPreview(prevRes.data);
    } catch (e) {
      toast.error("Failed to load timesheets: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [weekStart, weekEnd, payrollAllowed]);

  useEffect(() => { load(); }, [load]);
  // Auto-refresh so new tech clock-ins show without a manual reload.
  useEffect(() => { const id = setInterval(load, 60000); return () => clearInterval(id); }, [load]);

  const loadInbox = useCallback(async () => {
    try {
      const r = await getMiterMockInbox();
      setInbox(r.data || []);
    } catch { /* mock inbox is optional */ }
  }, []);

  useEffect(() => { if (showInbox) loadInbox(); }, [showInbox, loadInbox]);

  // Group timesheets by tech_id; one row per active tech even if no
  // entries (so a missing-week stands out).
  const techRows = useMemo(() => {
    const byTech = new Map();
    timesheets.forEach(ts => {
      if (!byTech.has(ts.tech_id)) byTech.set(ts.tech_id, []);
      byTech.get(ts.tech_id).push(ts);
    });
    return technicians.map(t => {
      const ts = (byTech.get(t.id) || []).sort((a, b) => (a.date || "").localeCompare(b.date || ""));
      const reviews = ts.map(t => ({ ts: t, review: autoReviewDay(t) }));
      const allGreen = reviews.length > 0 && reviews.every(r => r.review.ok);
      const totalHours = ts.reduce((s, r) => s + (r.tech_entered_hours || r.gps_hours || 0), 0);
      return { tech: t, timesheets: ts, reviews, allGreen, totalHours };
    });
  }, [timesheets, technicians]);

  const handleApprove = async (ts) => {
    try {
      await approveTimesheet(ts.id, { hours_choice: "tech", action: "approve" });
      toast.success(`Approved ${mdy(ts.date)}`);
      load();
    } catch (e) {
      toast.error("Approve failed: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleApproveAllGreen = async (row) => {
    const greens = row.reviews.filter(r => r.review.ok && (Number(r.ts.tech_entered_hours) || Number(r.ts.gps_hours) || 0) > 0 && !["approved", "ready_to_push", "pushed"].includes(r.ts.status));
    if (greens.length === 0) { toast.info("No clean rows to approve"); return; }
    try {
      await Promise.all(greens.map(r => approveTimesheet(r.ts.id, { hours_choice: "tech", action: "approve" })));
      toast.success(`Approved ${greens.length} day${greens.length > 1 ? "s" : ""}`);
      load();
    } catch {
      toast.error("Bulk approve failed — some rows may not have approved");
      load();
    }
  };

  const handlePushBatch = async () => {
    const mode = config?.mode || "dry_run";
    if (mode === "live") {
      const phrase = window.prompt('Type PUSH TO MITER to confirm a real production push.');
      if (phrase !== "PUSH TO MITER") { toast.error("Push cancelled — confirmation phrase did not match"); return; }
    }
    setPushing(true);
    try {
      const r = await pushMiterBatch(weekStart);
      const summary = `pushed ${r.data.pushed} · skipped ${r.data.skipped} · failed ${r.data.failed}`;
      if (r.data.failed > 0) toast.warning(`Push complete with failures (${summary})`);
      else toast.success(`Push complete (${summary})`);
      load();
      if (showInbox) loadInbox();
    } catch (e) {
      toast.error("Push failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setPushing(false);
    }
  };

  const shiftWeek = (n) => setWeekStart(prev => format(addDays(parseISO(prev), n * 7), "yyyy-MM-dd"));
  const thisWeekStart = format(startOfWeek(new Date(), { weekStartsOn: 1 }), "yyyy-MM-dd");
  const isThisWeek = weekStart === thisWeekStart;
  const [rowFilter, setRowFilter] = useState("all");
  const [search, setSearch] = useState("");
  const q = search.trim().toLowerCase();
  const shownRows = techRows.filter(r => deepMatch(r, q) && (rowFilter === "all"
    || (rowFilter === "empty" && r.timesheets.length === 0)
    || (rowFilter === "clean" && r.allGreen)
    || (rowFilter === "flagged" && r.timesheets.length > 0 && !r.allGreen)));
  const isLive = config?.mode === "live";
  const pushTitle = isLive ? "Push to Miter (LIVE)" : config?.mode === "sandbox" ? "Push to Miter (sandbox)" : "Simulate push (dry run)";

  return (
    <div className="flex flex-col gap-2" data-testid="timesheets-page">
      {/* Toolbar — search · pills · actions | ‹ Today › date · summary far right (bare row, grid below) */}
      <div className="flex shrink-0 flex-wrap items-center gap-2" data-testid="timesheets-toolbar">
        <div className="flex min-w-0 items-center gap-1.5 flex-nowrap [&>*]:shrink-0">
          <SearchBar value={search} onChange={setSearch} placeholder="Search..." width="w-[8.5rem] shrink-0" testId="timesheets-search" />
          <Segmented testId="timesheets-status-pills" value={rowFilter} onChange={setRowFilter} defaultKey="all"
            options={[["all", "All"], ["flagged", "Flagged"], ["clean", "Clean"], ["empty", "No time"]].map(([key, label]) => ({ key, label, testId: `ts-filter-${key}` }))} />
          {payrollAllowed && (
            <>
              <div className="h-4 w-px bg-slate-200" />
              <button type="button" className={`${btn("data")} h-8 w-8 justify-center !px-0`} title="Download this week as CSV" aria-label="Download CSV" onClick={() => {
                const url = `${process.env.REACT_APP_BACKEND_URL}/api/miter/sample-csv?week_start=${weekStart}`;
                const a = document.createElement("a");
                a.href = url; a.download = `miter-export-${weekStart}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
              }} data-testid="btn-export-csv">
                <Download className="w-4 h-4" />
              </button>
              <button type="button" onClick={handlePushBatch} disabled={pushing || !preview || preview.unpushed_count === 0} title={pushTitle}
                className={`${btn(isLive ? "danger" : "data")} h-8 w-8 justify-center !px-0`} aria-label={pushTitle} data-testid="btn-push-miter">
                {pushing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </button>
              <ModeChip mode={config?.mode || "dry_run"} />
              <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0 ${showInbox ? "bg-slate-100" : ""}`} onClick={() => setShowInbox(s => !s)} title="Miter mock inbox" aria-label="Miter mock inbox" data-testid="btn-toggle-mock-inbox">
                <Inbox className="w-4 h-4" />
              </button>
            </>
          )}
        </div>
        <div className="ml-auto flex items-center gap-1 shrink-0" data-testid="timesheets-toolbar-right">
          <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={() => shiftWeek(-1)} title="Previous week" data-testid="btn-week-prev"><ChevronLeft className="w-4 h-4" /></button>
          <button type="button" className={`${btn("nav")} h-8 !px-2`} onClick={() => setWeekStart(thisWeekStart)} disabled={isThisWeek} data-testid="btn-week-this">Today</button>
          <button type="button" className={`${btn("nav")} h-8 w-8 justify-center !px-0`} onClick={() => shiftWeek(1)} title="Next week" data-testid="btn-week-next"><ChevronRight className="w-4 h-4" /></button>
          <span style={{ "--datejump-minw": "0px" }}><DateJump testId="timesheets-date-jump" value={parseISO(weekStart)}
            onPick={(d) => setWeekStart(format(startOfWeek(d, { weekStartsOn: 1 }), "yyyy-MM-dd"))}
            label={`${format(parseISO(weekStart), "MMM d")} – ${format(parseISO(weekEnd), "MMM d, yyyy")}`} /></span>
          <div className="shrink-0 px-1 text-[11px] text-slate-500 num whitespace-nowrap" data-testid="timesheets-count"
            title={`${shownRows.length} of ${techRows.length} techs shown`}>
            {[
              payrollAllowed && preview ? `${preview.total_hours}h · ${preview.unpushed_count} ready · ${preview.blocked_count} blocked` : "",
              payrollAllowed && inbox.length ? `${inbox.length} in inbox` : "",
            ].filter(Boolean).join(" · ") || `${techRows.length} techs`}
          </div>
        </div>
      </div>

      <div className="data-table-container">
        {loading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
        ) : shownRows.length === 0 ? (
          <div className="text-center py-10 text-sm text-slate-400" data-testid="timesheets-empty">{techRows.length ? "No technicians match this filter." : "No active technicians."}</div>
        ) : (
          <div className="p-3">
            <div className="border border-slate-300 rounded-lg overflow-hidden" style={{ display: "grid", gridTemplateColumns: weekGridCols(weekDays.length), gridAutoRows: "min-content" }} data-testid="timesheets-week-grid">
              <WeekCornerHeader dense />
              {weekDays.map(d => <WeekDayHeader key={d.toISOString()} day={d} dense testId={`ts-day-${format(d, "yyyy-MM-dd")}`} />)}
              {shownRows.map(row => (
                <TechRow key={row.tech.id} row={row} weekDays={weekDays} onApprove={handleApprove} onApproveAllGreen={() => handleApproveAllGreen(row)} />
              ))}
            </div>
          </div>
        )}
      </div>

      {showInbox && (
        <div className="data-table-container" data-testid="mock-inbox-panel">
          <div className="data-table-header flex items-center justify-between">
            <div className="data-table-title">Miter Mock Inbox</div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">{inbox.length} captured payload{inbox.length === 1 ? "" : "s"}</span>
              <Button variant="ghost" size="sm" onClick={loadInbox}><RefreshCw className="w-3.5 h-3.5" /></Button>
              <Button variant="outline" size="sm" onClick={async () => { await clearMiterMockInbox(); loadInbox(); toast.success("Mock inbox cleared"); }} data-testid="btn-clear-mock-inbox">Clear</Button>
            </div>
          </div>
          {inbox.length === 0 ? (
            <div className="text-center py-8 text-sm text-slate-400">Empty. Run a push in SANDBOX mode to populate.</div>
          ) : (
            <div className="p-3 space-y-2 max-h-[400px] overflow-y-auto">
              {inbox.map(rec => (
                <details key={rec.id} className="border border-slate-200 rounded">
                  <summary className="px-3 py-2 text-xs cursor-pointer hover:bg-slate-50 flex items-center gap-2">
                    <span className="font-mono text-slate-400">{rec.id}</span>
                    <span className="font-medium">{rec.payload?.team_member_id || "—"}</span>
                    <span className="text-slate-500">{rec.payload?.clock_in?.slice(0, 10) || ""}</span>
                    <span className="text-slate-500">{rec.payload?.job || ""}</span>
                    <span className="ml-auto text-slate-400">{rec.received_at?.slice(0, 19).replace("T", " ")}</span>
                  </summary>
                  <pre className="px-3 py-2 text-[11px] bg-slate-50 overflow-x-auto">{JSON.stringify(rec.payload, null, 2)}</pre>
                </details>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const STATUS_LABEL = { open: "Open", submitted: "Submitted", flagged: "Flagged", approved: "Approved", ready_to_push: "Ready", pushed: "Pushed", rejected: "Rejected" };
const mdy = (iso) => { try { return format(parseISO(iso), "M/d/yy"); } catch { return iso; } };

function TechRow({ row, weekDays, onApprove, onApproveAllGreen }) {
  const { tech, timesheets, reviews, totalHours } = row;
  const tsByDate = useMemo(() => {
    const m = {};
    timesheets.forEach(ts => { m[ts.date] = ts; });
    return m;
  }, [timesheets]);
  const flaggedCount = reviews.filter(r => !r.review.ok && !["approved", "ready_to_push", "pushed"].includes(r.ts.status)).length;
  const canApproveAll = timesheets.some(ts => !["approved", "ready_to_push", "pushed"].includes(ts.status));
  const flagged = reviews.filter(r => !r.review.ok && !["approved", "ready_to_push", "pushed"].includes(r.ts.status));

  return (
    <>
      <TechCell tech={tech} subtitle={tech.miter_department || "— no department —"} testId={`timesheet-row-${tech.id}`}
        right={canApproveAll && (
          <button type="button" onClick={onApproveAllGreen} title="Approve every clean day this week" aria-label="Approve all green"
            className={`${btn("write")} w-6 h-6 justify-center !px-0`} data-testid={`btn-approve-green-${tech.id}`}>
            <CheckCircle2 className="w-3.5 h-3.5" />
          </button>
        )}>
        <div className="flex items-center gap-1.5 pl-4 text-[10px]">
          <span className="font-semibold text-slate-700 num">{totalHours.toFixed(1)}h</span>
          {timesheets.length === 0 ? (
            <span className="text-slate-300">no time</span>
          ) : flaggedCount > 0 ? (
            <span className="inline-flex items-center rounded-full px-1.5 py-0 font-semibold bg-rose-100 text-rose-700" data-testid={`indicator-red-${tech.id}`}>{flaggedCount} flag{flaggedCount === 1 ? "" : "s"}</span>
          ) : (
            <span className="inline-flex items-center rounded-full px-1.5 py-0 font-semibold bg-emerald-100 text-emerald-700" data-testid={`indicator-green-${tech.id}`}>clean</span>
          )}
        </div>
        {flagged.length > 0 && (
          <ul className="pl-4 space-y-0.5" data-testid={`flag-reasons-${tech.id}`}>
            {flagged.map(r => (
              <li key={r.ts.id} className="text-[9px] text-rose-600 leading-tight"><span className="text-slate-400 num">{mdy(r.ts.date)}</span> · {r.review.reasons.join(" · ")}</li>
            ))}
          </ul>
        )}
      </TechCell>
      {weekDays.map(d => {
        const key = format(d, "yyyy-MM-dd");
        const ts = tsByDate[key];
        const review = ts ? autoReviewDay(ts) : null;
        const ok = review?.ok;
        const today = key === format(new Date(), "yyyy-MM-dd");
        return (
          <div key={key} className={`p-1.5 border-b border-r border-slate-300 last:border-r-0 text-[11px] min-h-[64px] ${today ? "bg-blue-50/20" : "bg-white"}`} data-testid={`day-cell-${tech.id}-${key}`}>
            {ts ? (
              <div className={`rounded-md border px-1.5 py-1 ${ok ? "border-emerald-200 bg-emerald-50/50" : "border-rose-200 bg-rose-50/50"}`}>
                <div className="flex items-center justify-between gap-1">
                  <span className="font-bold num">{(ts.tech_entered_hours || ts.gps_hours || 0).toFixed(1)}h</span>
                  <span className={`inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-semibold ${STATUS_BADGE[ts.status] || "bg-slate-100 text-slate-600"}`}>{STATUS_LABEL[ts.status] || ts.status}</span>
                </div>
                {ts.notes && <div className="mt-0.5 text-[9px] text-slate-500 italic truncate" title={ts.notes} data-testid={`ts-notes-${tech.id}-${key}`}>{ts.notes}</div>}
                {!["approved", "ready_to_push", "pushed"].includes(ts.status) && (
                  <button onClick={() => onApprove(ts)} className="mt-1 text-[9px] font-semibold text-emerald-700 hover:underline" data-testid={`btn-approve-${tech.id}-${key}`}>Approve</button>
                )}
              </div>
            ) : null}
          </div>
        );
      })}
    </>
  );
}
